import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  message,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Alert,
  Switch,
  Divider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  CalendarOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface Company {
  id: number;
  name: string;
  short_name: string;
  email: string;
  phone: string;
}

interface TaxLegislation {
  id: number;
  country_code: string;
  country_name: string;
  currency_code: string;
  currency_symbol: string;
  is_default: boolean;
  is_active: boolean;
  effective_date: string;
}

interface CompanyLegislation {
  id: number;
  company_id: number;
  legislation_id: number;
  is_primary: boolean;
  effective_date: string;
  notes: string;
  created_at: string;
  updated_at: string;
  // Relations
  company_name: string;
  company_short_name: string;
  country_name: string;
  country_code: string;
  currency_symbol: string;
  legislation_is_default: boolean;
}

interface CompanyLegislationManagerProps {
  companyId?: number; // Si fourni, mode spécifique à une entreprise
}

const CompanyLegislationManager: React.FC<CompanyLegislationManagerProps> = ({
  companyId,
}) => {
  const { user } = useAuth();
  const isSysAdmin = Boolean(user?.is_superadmin);
  const [assignments, setAssignments] = useState<CompanyLegislation[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [legislations, setLegislations] = useState<TaxLegislation[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] =
    useState<CompanyLegislation | null>(null);
  const [form] = Form.useForm();

  // Charger les données au montage
  useEffect(() => {
    fetchAssignments();
    if (isSysAdmin) {
      fetchCompanies();
      fetchLegislations();
    }
  }, [companyId, isSysAdmin]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const url = companyId
        ? `/api/hr/tax-legislations/companies/${companyId}/assignments`
        : "/api/hr/tax-legislations/companies/assignments";

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setAssignments(data.data);
      } else {
        message.error(
          data.message || "Erreur lors du chargement des assignations"
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors du chargement des assignations");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    if (companyId) return; // Pas besoin si mode spécifique à une entreprise

    try {
      console.log("🔍 Chargement des entreprises...");
      const token = localStorage.getItem("authToken");
      const response = await fetch("/api/companies", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      console.log("📦 Réponse API companies:", data);

      // L'API /api/companies retourne {companies: [...], pagination: {...}}
      if (data.companies) {
        console.log(`✅ ${data.companies.length} entreprises chargées`);
        setCompanies(data.companies);
      } else {
        console.error("❌ Format de réponse inattendu:", data);
        message.error("Erreur lors du chargement des entreprises");
      }
    } catch (error) {
      console.error("❌ Erreur lors du chargement des entreprises:", error);
      message.error("Erreur lors du chargement des entreprises");
    }
  };

  const fetchLegislations = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        "/api/hr/tax-legislations?active_only=true",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setLegislations(data.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des législations:", error);
    }
  };

  const handleCreate = () => {
    if (!isSysAdmin) return;
    setEditingAssignment(null);
    form.resetFields();
    if (companyId) {
      form.setFieldsValue({ company_id: companyId });
    }
    setShowModal(true);
  };

  const handleEdit = (assignment: CompanyLegislation) => {
    if (!isSysAdmin) return;
    setEditingAssignment(assignment);
    form.setFieldsValue({
      company_id: assignment.company_id,
      legislation_id: assignment.legislation_id,
      is_primary: assignment.is_primary,
      effective_date: dayjs(assignment.effective_date),
      notes: assignment.notes,
    });
    setShowModal(true);
  };

  const handleDelete = (assignment: CompanyLegislation) => {
    if (!isSysAdmin) return;
    Modal.confirm({
      title: "Confirmer la suppression",
      content: `Êtes-vous sûr de vouloir supprimer l'assignation de la législation "${assignment.country_name}" pour l'entreprise "${assignment.company_name}" ?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `/api/hr/tax-legislations/companies/assignments/${assignment.id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          const data = await response.json();
          if (data.success) {
            message.success("Assignation supprimée avec succès");
            fetchAssignments();
          } else {
            message.error(data.message || "Erreur lors de la suppression");
          }
        } catch (error) {
          console.error("Erreur:", error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    if (!isSysAdmin) return;
    try {
      const token = localStorage.getItem("authToken");
      const url = editingAssignment
        ? `/api/hr/tax-legislations/companies/assignments/${editingAssignment.id}`
        : "/api/hr/tax-legislations/companies/assignments";

      const method = editingAssignment ? "PUT" : "POST";
      const payload = {
        company_id: values.company_id || companyId, // Utiliser companyId si values.company_id est undefined
        legislation_id: values.legislation_id,
        is_primary: values.is_primary || false,
        effective_date: values.effective_date.format("YYYY-MM-DD"),
        notes: values.notes || null, // Convertir undefined en null
      };

      console.log("📤 Payload envoyé:", payload); // Debug

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success(
          editingAssignment
            ? "Assignation mise à jour avec succès"
            : "Assignation créée avec succès"
        );
        setShowModal(false);
        fetchAssignments();
      } else {
        message.error(data.message || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors de la sauvegarde");
    }
  };

  const handleTogglePrimary = async (assignment: CompanyLegislation) => {
    if (!isSysAdmin) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `/api/hr/tax-legislations/companies/assignments/${assignment.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...assignment,
            is_primary: !assignment.is_primary,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        message.success("Statut principal mis à jour avec succès");
        fetchAssignments();
      } else {
        message.error(data.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  // Colonnes du tableau
  const columns: ColumnsType<CompanyLegislation> = [
    ...(companyId
      ? []
      : [
          {
            title: "Entreprise",
            key: "company",
            width: 200,
            render: (_: any, record: CompanyLegislation) => (
              <div>
                <div style={{ fontWeight: 500 }}>{record.company_name}</div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {record.company_short_name}
                </Text>
              </div>
            ),
          },
        ]),
    {
      title: "Législation",
      key: "legislation",
      width: 200,
      render: (_: any, record: CompanyLegislation) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BankOutlined style={{ color: "#1890ff" }} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.country_name}</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.country_code} • {record.currency_symbol}
            </Text>
          </div>
          {record.legislation_is_default && (
            <Tag color="gold" style={{ fontSize: "10px" }}>
              DÉFAUT
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Date effective",
      dataIndex: "effective_date",
      key: "effective_date",
      width: 120,
      render: (date: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <CalendarOutlined style={{ color: "#faad14" }} />
          <span>{new Date(date).toLocaleDateString("fr-FR")}</span>
        </div>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 120,
      render: (_: any, record: CompanyLegislation) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Switch
            checked={record.is_primary}
            onChange={() => handleTogglePrimary(record)}
            disabled={!isSysAdmin}
            size="small"
            checkedChildren="Principal"
            unCheckedChildren="Secondaire"
          />
        </div>
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (notes: string) => (
        <Tooltip title={notes || "Aucune note"}>
          <Text ellipsis style={{ maxWidth: 200 }}>
            {notes || "-"}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: CompanyLegislation) =>
        isSysAdmin ? (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              size="small"
            />
          </Space>
        ) : (
          <Text type="secondary">Lecture seule</Text>
        ),
    },
  ];

  return (
    <div style={{ padding: companyId ? 0 : "24px" }}>
      {/* En-tête */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              {companyId
                ? "Législations fiscales assignées"
                : "Gestion des assignations législation-entreprise"}
            </Title>
            <Text type="secondary">
              {companyId
                ? "Législations fiscales appliquées à cette entreprise"
                : "Assignation des législations fiscales aux entreprises"}
            </Text>
          </Col>
          {isSysAdmin ? <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvelle assignation
            </Button>
          </Col> : null}
        </Row>
      </Card>

      {/* Alerte d'information */}
      {!companyId && (
        <Alert
          message="Gestion des législations par entreprise"
          description="Chaque entreprise peut avoir plusieurs législations fiscales, mais une seule peut être marquée comme principale. La législation principale sera utilisée par défaut pour les calculs de paie."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {!isSysAdmin && (
        <Alert
          message="Consultation uniquement"
          description="L'attribution des législations fiscales est réservée au SysAdmin."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Tableau des assignations */}
      <Card>
        <Table
          columns={columns}
          dataSource={assignments}
          rowKey="id"
          loading={loading}
          pagination={{
            total: assignments.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} assignations`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal de création/édition */}
      <Modal
        title={
          editingAssignment ? "Modifier l'assignation" : "Nouvelle assignation"
        }
        open={isSysAdmin && showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_primary: false,
            effective_date: dayjs(),
          }}
        >
          {!companyId && (
            <Form.Item
              label="Entreprise"
              name="company_id"
              rules={[{ required: true, message: "Entreprise obligatoire" }]}
            >
              <Select
                placeholder="Sélectionner une entreprise"
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name} ({company.short_name})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Législation fiscale"
            name="legislation_id"
            rules={[{ required: true, message: "Législation obligatoire" }]}
          >
            <Select
              placeholder="Sélectionner une législation"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()) ?? false
              }
            >
              {legislations.map((legislation) => (
                <Option key={legislation.id} value={legislation.id}>
                  {legislation.country_name} ({legislation.country_code}) -{" "}
                  {legislation.currency_symbol}
                  {legislation.is_default && " [DÉFAUT]"}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Date effective"
            name="effective_date"
            rules={[{ required: true, message: "Date effective obligatoire" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="Sélectionner la date"
            />
          </Form.Item>

          <Form.Item
            label="Législation principale"
            name="is_primary"
            valuePropName="checked"
          >
            <Switch checkedChildren="Oui" unCheckedChildren="Non" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea
              rows={3}
              placeholder="Notes spécifiques à cette assignation..."
            />
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setShowModal(false)}>Annuler</Button>
              <Button type="primary" htmlType="submit">
                {editingAssignment ? "Mettre à jour" : "Créer"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyLegislationManager;
